import BigNumber from "bignumber.js";
import dayjs from "dayjs";

import { BN } from "./BN";

export interface FormatOptions {
  compact?: boolean;
  toSignificant?: boolean; // If true, formats to significant digits
  minThreshold?: number; // Optional min threshold (def: 1e-6)
  decimals?: number;
  formatSettings?: BigNumber.Format;
}

interface FormatUnit {
  threshold: number;
  divisor: number;
  suffix: string;
}

const defaultUnits: FormatUnit[] = [
  { threshold: 1e12, divisor: 1e12, suffix: "T" },
  { threshold: 1e9, divisor: 1e9, suffix: "B" },
  { threshold: 1e6, divisor: 1e6, suffix: "M" },
  { threshold: 1e3, divisor: 1e3, suffix: "K" },
  { threshold: 0, divisor: 1, suffix: "" },
];

// Utility functions for formatting numbers, dates, USD, and percent values

const defaultFormatOptions: FormatOptions = {
  compact: false,
  toSignificant: true,
  minThreshold: 1e-9,
  decimals: 2,
  formatSettings: {
    groupSeparator: ",",
    decimalSeparator: ".",
    groupSize: 3,
    prefix: "",
    suffix: "",
  },
};

const toSpecificFormat = (value: BN, decimals: number, options: FormatOptions = {}): string => {
  if (options.toSignificant) {
    return value.toSignificant(decimals, BigNumber.ROUND_DOWN, options.formatSettings);
  }

  return value.toFormat(decimals, BigNumber.ROUND_DOWN, options.formatSettings);
};

const formatWithUnits = (value: BN, units: FormatUnit[], formatSettings?: BigNumber.Format, decimals = 2): string => {
  const absValue = value.abs();
  for (const unit of units) {
    if (absValue.gte(unit.threshold)) {
      return `${absValue.div(unit.divisor).toSignificant(decimals, BigNumber.ROUND_DOWN, formatSettings)}${unit.suffix}`;
    }
  }
  return absValue.toSignificant(decimals, BigNumber.ROUND_DOWN, formatSettings);
};

const checkMinThreshold = (value: BN, minThreshold = 1e-6): string | null => {
  if (value.isZero()) return null;
  return value.abs().lt(minThreshold) ? "0" : null;
};

const mergeFormatOptions = (defaults: FormatOptions, user: Partial<FormatOptions> = {}): FormatOptions => ({
  ...defaults,
  ...user,
  formatSettings: {
    ...defaults.formatSettings,
    ...user.formatSettings,
  },
});

export const formatNumber = (value: BN | undefined, options: FormatOptions = {}, defaultValue = "-"): string => {
  if (!value || value.isNaN()) return defaultValue;
  const _options = mergeFormatOptions(defaultFormatOptions, options);
  const thresholdResult = checkMinThreshold(value, _options.minThreshold);
  if (thresholdResult) return thresholdResult;
  const decimals = _options.decimals ?? 2;
  if (_options.compact) return formatWithUnits(value, defaultUnits, _options.formatSettings, decimals);
  return toSpecificFormat(value, decimals, _options);
};

export const formatAmount = (value: BN, decimals = 18, options: FormatOptions = {}) => {
  return formatNumber(BN.formatUnits(value, decimals), options);
};

export const formatDate = (date: Date | number | string, format: string) => {
  return dayjs(date).format(format);
};

export const parseCreatedAt = (value: string | number | Date | undefined | null): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string") return new Date(value.replace(/\.\d+$/, ""));
  // Fallback for unexpected types
  return new Date();
};

export const formatDuration = (duration: number, format: string) => {
  return dayjs.duration(duration, "milliseconds").format(format);
};

export const formatPrice = (val: string | undefined, maxDigits = 6) => {
  if (val === undefined || val === null || val === "") return "-";
  const num = Number(val);
  if (Number.isNaN(num)) return String(val);

  const integerPart = Math.floor(Math.abs(num));
  const integerDigits = integerPart === 0 ? 1 : Math.floor(Math.log10(integerPart)) + 1;
  const decimalDigits = Math.max(2, maxDigits - integerDigits);

  return num.toFixed(decimalDigits);
};

export const formatUsd = (value: BN | number | string, options: FormatOptions = {}) => {
  return formatNumber(
    new BN(value),
    mergeFormatOptions(
      {
        formatSettings: { prefix: "$" },
      },
      options,
    ),
  );
};

export const formatPercent = (value: BN | number | string, asFraction = false, decimals = 3) => {
  const result = asFraction ? new BN(value).multipliedBy(100) : new BN(value);
  return formatNumber(result, {
    decimals,
    toSignificant: false,
    formatSettings: {
      groupSeparator: "",
      decimalSeparator: ".",
      suffix: "%",
    },
  });
};
